import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { stringFormat } from '@brycemarshall/string-format';
import { Client, Contact } from '@models/profile/model';
import dayjs from 'dayjs';
import { Observable, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs';
import { Model } from 'src/app/shared/models/clux/model-legacy';
import { ClientQuery, ContactQuery } from 'src/app/state';

import { CommonConstants, UploadDataFileType } from '../../../shared/constants/common.constant';
import { EmitterService } from '../../../shared/services/emitter.service';
import { Categories } from '../../constants/analyticsCategory.constant';
import { EmitterMessage } from '../../models/clux/model';
import { SearchCriteria } from '../../models/uba/configuration/model';
import { Attachment, AttachmentUrlResponse, ChainType, MatchType } from '../../models/uba/file/model';
import { Document } from '../../models/uba/profileConfiguration/model';
import { GoogleAnalyticsService } from '../../services/google-analytics.service';
import { ServiceFactory } from '../../services/service.factory';
import { SharedService } from '../../services/shared.service';
import { RouteContextProviderService } from '../../utils/routecontextprovider.service';
import { DialogComponent } from '../upload-file/upload-file-messages/dialog.component';
import { NgClass } from '@angular/common';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { PopoverTooltipComponent } from '../popover-tooltip/popover-tooltip.component';
import { MatButton } from '@angular/material/button';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { HighlightDirective } from '../../directives/highlight.directive';
import { UserAccessUtil } from '@app/shared/utils/userAccess.util';

@Component({
  selector: 'app-upload-file',
  templateUrl: './upload-file.component.html',
  styleUrls: ['../add-enroll-employee/add-enroll-employee.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, MatFormField, MatSelect, FormsModule, MatOption, PopoverTooltipComponent, ReactiveFormsModule, MatButton, MatSlideToggle, MatLabel, MatInput, HighlightDirective],
})
export class UploadFileComponent implements OnInit, OnDestroy {
  private clientQuery = inject(ClientQuery);
  private contactQuery = inject(ContactQuery);
  private dialog = inject(MatDialog);
  private formBuilder = inject(UntypedFormBuilder);
  private emitterService = inject(EmitterService);
  private routecontextprovider = inject(RouteContextProviderService);
  public serviceFactory = inject(ServiceFactory);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private sharedService = inject(SharedService);

  @Input() public fileBrowse: Model.FileBrowse | Model.BrowseView;
  @Output() public oncancel: EventEmitter<string> = new EventEmitter();
  @Output() public fileUploadSuccess: EventEmitter<boolean> = new EventEmitter();
  public fileName: string;
  public onFiledragOver: boolean;
  public isError: boolean;
  public fileUploadForm: UntypedFormGroup;
  public fileTypes: Model.UploadFileTypes[];
  public selectedFileType: string;
  public dataFileInfoText: string;
  public subscription = new Subscription();
  public isDataFile: boolean;
  public fileAttachementID: string;
  public isDocumentFile: boolean;
  public isVerifyPosting: boolean;
  public fileTextMessage: string;

  private file: File;
  private newFile: Model.UploadFileForm;
  private contact: Contact;
  private dialogRef: MatDialogRef<object>;
  private client: Client;

  public ngOnInit(): void {
    this.initializeModel();
    this.setDefaultForm();
    this.getFileTypes();
    this.fileTextMessage = this.sharedService.getMessageByBrand(this.fileBrowse.fileRenameText);
  }

  public browseFile(event: Event): void {
    if (this.contact) {
      const target = event.target as HTMLInputElement;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.file = (target.files as FileList | any)[0];
      this.fileName = this.file.name;
      this.newFile.file = this.file;
      this.onFiledragOver = true;
      if (this.isDocumentFile) {
        this.validateDoumentFile(this.fileName);
      } else {
        this.validateFile(this.fileName);
      }
    }
  }

  public upload(formData: Model.FileFormData): void {
    /**
     * Track 'MyDocumentsUpload and DataFileUpload' event
     */
    const uploadHeader = this.fileBrowse.uploadHeader;
    if (uploadHeader === CommonConstants.fileUpload.dataFileUpload.uploadHeader) {
      const eventCategory: Model.EventCategory = Object.assign({}, Categories.EventCategories.DataFileUpload);
      eventCategory.eventLabel = eventCategory.eventLabel + this.newFile.fileType;
      this.googleAnalyticsService.eventEmitter(eventCategory);
    }
    if (uploadHeader === CommonConstants.fileUpload.documentFile.uploadHeader) {
      this.googleAnalyticsService.eventEmitter(Categories.EventCategories.MyDocumentsUpload);
    }
    if (uploadHeader === CommonConstants.fileUpload.enrollEmployees.fileBrowse.uploadHeader) {
      this.googleAnalyticsService.eventEmitter(Categories.EventCategories.EnrollMultipeEmployees);
    }
    this.openDialog('uploadingFile');
    if (this.isDocumentFile) {
      this.uploadDocument(formData);
    } else {
      this.uploadCSVFile(formData);
    }
  }

  public uploadDocument(formData: Model.FileFormData): void {
    const fileType: string = this.fileName.substr(this.fileName.lastIndexOf('.') + 1);
    const file: Model.UploadFileForm = this.newFile;
    const profileId: string = this.contact.clientId;
    this.subscription.add(
      this.serviceFactory.uploadDocument(file.file, profileId, this.fileName, fileType, formData.fileDescription).subscribe({
        next: () => {
          this.dialogRef.close();
          const message: EmitterMessage = this.emitterService.getEmitterMessage(this.isDataFile ? CommonConstants.errorCodes.dataFileUploadSuccess : CommonConstants.errorCodes.fileUploadSuccess, true);
          this.emitterService.on(CommonConstants.emitterKeys.loginErrors).emit(message);
          this.fileUploadSuccess.emit(true);
        },
        error: () => {
          this.dialogRef.close();
          const message: EmitterMessage = this.emitterService.getEmitterMessage(CommonConstants.errorCodes.fileUploadError, false);
          this.emitterService.on(CommonConstants.emitterKeys.loginErrors).emit(message);
          window.scroll(0, 0);
        },
        complete: () => {
          this.dialogRef.close();
        },
      })
    );
  }

  public onCancel(): void {
    this.oncancel.emit();
  }

  public onFileDragOver(event: Event): void {
    event.preventDefault();
    this.onFiledragOver = true;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    if (this.contact) {
      const target = event.dataTransfer as DataTransfer;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.file = (target.files as FileList | any)[0];
      this.fileName = this.file.name;
      this.newFile.file = this.file;
      if (this.isDocumentFile) {
        this.validateDoumentFile(this.fileName);
      } else {
        this.validateFile(this.fileName);
      }
    }
  }

  public removeFile(): void {
    this.fileName = null;
    this.onFiledragOver = this.isError = false;
    this.fileUploadForm.reset();
  }

  public onChange(option: MatSelectChange): void {
    this.fileBrowse.fileType = option.value;
    this.newFile.fileType = option.value;
    if (this.fileName) {
      this.validateFile(this.fileName);
    }
  }

  public isDisable(): boolean {
    return this.isError && !this.isDocumentFile ? false : this.isDocumentFile && this.fileUploadForm.value.fileDescription && this.fileUploadForm.value.fileDescription.length > 0 ? false : true;
  }

  public downloadFile(): void {
    this.subscription.add(
      this.getDownloadFileUrl(this.fileAttachementID).subscribe((response) => {
        if (response) {
          this.routecontextprovider.navigateTo(response, '_self');
        }
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private setDefaultForm(): void {
    this.fileUploadForm = this.formBuilder.group({
      file: ['', Validators.required],
      testFile: [false],
      fileDescription: ['', this.isDocumentFile ? Validators.required : null],
    });
  }

  private initializeModel(): void {
    this.newFile = {
      fileType: this.fileBrowse.fileType,
      fileDirty: false,
      file: null,
      isTest: false,
      renameFile: '',
    };
    if (sessionStorage.CL_LUD !== 'undefined') {
      this.contact = this.contactQuery.getActive();
      this.client = this.clientQuery.getActive();
    }
    if (this.fileBrowse.uploadHeader === CommonConstants.fileUpload.dataFileUpload.uploadHeader) {
      this.isDataFile = true;
      if (this.client.divisionBasedAccess && UserAccessUtil.getDivisionBasedDataFileAccessRoles(this.contact)) {
        this.fileTypes = UploadDataFileType.filter((fileType) => CommonConstants.allowedFileTypesForDataFileAccess.includes(fileType.name));
      } else {
        this.fileTypes = UploadDataFileType;
      }
      this.selectedFileType = this.fileTypes[0].value;
      this.fileBrowse.fileType = this.fileTypes[0].value;
      this.newFile.fileType = this.fileTypes[0].value;
      this.dataFileInfoText = 'Coming soon...';
    }
    this.isDocumentFile = this.fileBrowse.uploadHeader === CommonConstants.fileUpload.documentFile.uploadHeader ? true : false;
    this.isVerifyPosting = this.fileBrowse.uploadHeader === CommonConstants.fileUpload.verifyPostingFileUpload.uploadHeader ? true : false;
  }

  private uploadCSVFile(formData: Model.FileFormData): void {
    const fileData: Model.FileData = {
      renameFile: this.newFile.renameFile,
      fileType: this.newFile.fileType,
      isTest: formData.testFile,
    };
    const fileInfo: Model.FileInfo = this.serviceFactory.createFileInfo(fileData);
    const filePayload: Model.FilePayload = {
      fileInfo,
      command: this.serviceFactory.createCommand(fileInfo, 'StartToPending'),
    };
    const servicePath: string = stringFormat(CommonConstants.getPath.file.uploadProcess, {
      userId: this.contact.id,
      fileId: filePayload.fileInfo.id,
    });

    this.subscription.add(
      this.serviceFactory.uploadFile<string>(this.newFile.file, CommonConstants.getPath.file.serviceKey, servicePath, filePayload).subscribe({
        next: () => {
          this.dialogRef.close();
          const message: EmitterMessage = this.emitterService.getEmitterMessage(this.isDataFile ? CommonConstants.errorCodes.dataFileUploadSuccess : CommonConstants.errorCodes.fileUploadSuccess, true);
          this.emitterService.on(CommonConstants.emitterKeys.loginErrors).emit(message);
          this.fileUploadSuccess.emit(true);
        },
        error: () => {
          this.dialogRef.close();
          const message: EmitterMessage = this.emitterService.getEmitterMessage(CommonConstants.errorCodes.fileUploadError, false);
          this.emitterService.on(CommonConstants.emitterKeys.loginErrors).emit(message);
          window.scroll(0, 0);
        },
      })
    );
  }

  private validateFile(fileName: string): void {
    this.isError = /\.(csv)$/i.test(fileName);
    if (!this.isError || this.file.size / 1024 / 1024 > 20) {
      this.openDialog('errorAtUploadFile');
      this.onFiledragOver = false;
      this.fileName = null;
    } else {
      if (this.fileBrowse.fileType === 'PostingVerification') {
        this.fileName = this.client.externalId + '_' + `${this.fileBrowse.fileType}` + '_' + dayjs(new Date()).format('YYYY-MM-DD') + '.csv';
      } else {
        this.fileName = (this.client.externalId + '_' + `${this.fileBrowse.fileType}` + '_' + dayjs(new Date()).format('YYYY-MM-DD') + '.csv').toUpperCase();
      }
      this.newFile.renameFile = this.fileName;
    }
  }

  private validateDoumentFile(_fileName: string): void {
    this.isError = true;
    if (this.file.size / 1024 / 1024 > 20) {
      this.openDialog('errorAtUploadFile');
      this.onFiledragOver = false;
    } else {
      this.newFile.renameFile = this.fileName;
    }
  }

  private openDialog(dialogType: 'errorAtUploadFile' | 'uploadingFile'): void {
    const fileInfo = this.fileBrowse.dialogData[dialogType];
    this.dialogRef = this.dialog.open(DialogComponent, {
      disableClose: true,
      data: {
        title: fileInfo.title,
        desc: fileInfo.desc,
        desc1: this.isErrorAtUploadFileText(fileInfo) ? fileInfo.desc1 : undefined,
        desc2: this.isDataFile ? this.fileBrowse.dialogData.errorAtUploadFile.desc2.replace('type', this.fileBrowse.fileType) : this.isErrorAtUploadFileText(fileInfo) ? fileInfo.desc2 : undefined,
        view: fileInfo.view,
        fileName: this.fileName,
      },
    });
  }

  private getFileTypes(): void {
    const queryParams: Model.QueryParams = {
      profileId: this.contactQuery.getActive().clientId,
    };
    const searchParams: Model.SearchParams = {
      take: 100,
      skip: 0,
      orderBy: 'effectiveDate',
      orderDirection: 'desc',
    };
    const fileRequestPayload: SearchCriteria = [
      {
        key: 'documentType',
        value: 'FileTemplate',
        matchType: MatchType.EXACT,
        chainType: ChainType.AND,
      },
    ];
    this.subscription.add(
      this.serviceFactory.search<Document[]>(CommonConstants.getPath.profile.configurationKey, CommonConstants.getPath.profile.documents, fileRequestPayload, queryParams, searchParams).subscribe((response) => {
        const fileTypeResponse: Document[] = response.data;
        const fileTypeId: Document = fileTypeResponse.find((item) => {
          return item.name.toUpperCase() === this.fileBrowse.fileType.toUpperCase();
        });
        this.fileAttachementID = fileTypeId && fileTypeId.documentAttachmentId;
      })
    );
  }

  private getDownloadFileUrl(fileId: string): Observable<string | null> {
    const filePath: string = stringFormat(CommonConstants.getPath.file.downloadFile, {
      profileId: this.contact.parentId,
      attachmentId: fileId,
    });
    return this.serviceFactory.query(CommonConstants.getPath.file.serviceKey, filePath).pipe(
      switchMap((attachment: Attachment) => {
        const signedUrlPath: string = stringFormat(CommonConstants.getPath.file.getSignedUrl, {
          profileId: this.contact.parentId,
          attachmentId: fileId,
          contentType: 'application/' + attachment.attachmentType,
          fileName: encodeURIComponent(attachment.friendlyFileName),
        });
        return this.serviceFactory.query<AttachmentUrlResponse>(CommonConstants.getPath.file.serviceKey, signedUrlPath).pipe(map((res) => (res?.sync && res?.url ? res.url : null)));
      })
    );
  }

  private isErrorAtUploadFileText(value: Model.ErrorAtUploadFileText | Model.UploadingFileText): value is Model.ErrorAtUploadFileText {
    return 'desc1' in value;
  }
}
